import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { mapConversation } from '../lib/chatMappers';

/**
 * The conversation list, backed by GET /api/chat/conversations.
 *
 * The server returns one row per COUNTERPARTY (it deduplicates and sums unread
 * across every thread with that person), so this hook does no grouping of its
 * own - `id` is simply the most recent conversation with them.
 *
 * The backend scopes the list by the JWT role: a BUYER sees only threads where
 * they are the buyer, a SELLER only where they are the seller. `side` therefore
 * reports which inbox the server is actually giving us; it is not a filter we
 * can flip from the client.
 */
export function useInbox({ enabled, socket, currentUserId, side = 'buyer' }) {
  const [conversations, setConversations] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  /**
   * A conversation opened by deep link that is NOT in the inbox list.
   *
   * The list is deduplicated by counterparty (the server returns the most recent
   * thread per person), so a `?c=` pointing at an older thread - or a `?o=` for an
   * order whose thread is not the latest - has no row to select. This holds the
   * resolved conversation so the header and thread still work.
   */
  const [focusOverride, setFocusOverride] = useState(null);
  const deepLinkHandled = useRef(false);

  /**
   * Chats this account hid from its inbox (recoverable).
   *
   * Deleting a chat only marks our side; the thread stays in the database, so it
   * can always be restored. This list is the recovery surface - it is what stops
   * a chat from "disappearing and never coming back".
   */
  const [archivedChats, setArchivedChats] = useState([]);

  const loadArchived = useCallback(async () => {
    try {
      const data = await api.get('/api/chat/conversations/archived');
      setArchivedChats(data?.conversations ?? []);
    } catch {
      // Never let the recovery list break the inbox.
      setArchivedChats([]);
    }
  }, []);


  const selectedIdRef = useRef(null);
  selectedIdRef.current = selectedId;

  const refresh = useCallback(async () => {
    try {
      const data = await api.get('/api/chat/conversations');
      setConversations((data?.conversations ?? []).map(mapConversation));
      setStatus('ready');
      setError(null);
    } catch (requestError) {
      setError(requestError);
      setStatus('error');
    }
  }, []);

  const restoreChat = useCallback(
    async (conversationId) => {
      if (!conversationId) return;
      setArchivedChats((current) => current.filter((c) => c.conversationId !== conversationId));
      try {
        await api.post(`/api/chat/conversations/${conversationId}/restore`);
      } catch {
        await loadArchived();
        return;
      }
      await refresh();
      await loadArchived();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadArchived, refresh],
  );

  const restoreAllChats = useCallback(async () => {
    try {
      await api.post('/api/chat/conversations/restore-all');
    } catch {
      await loadArchived();
      return;
    }
    setArchivedChats([]);
    await refresh();
    await loadArchived();
  }, [loadArchived, refresh]);

  useEffect(() => {
    if (!enabled) return;
    loadArchived();
  }, [enabled, loadArchived]);
  useEffect(() => {
    if (!enabled) return;
    setStatus((current) => (current === 'ready' ? current : 'loading'));
    refresh();
  }, [enabled, refresh]);

  const markAsRead = useCallback(async (conversationId) => {
    if (!conversationId) return;
    // Optimistic: the row clears immediately, the POST confirms it.
    setConversations((current) =>
      current.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0, unread: false } : c)),
    );
    try {
      await api.post(`/api/chat/conversations/${conversationId}/mark-read`);
    } catch {
      /* a failed read receipt is not worth surfacing */
    }
  }, []);

  /* Live updates. The server broadcasts every new message to BOTH participants,
     so this has to ignore our own sends and avoid double-counting the thread we
     are currently reading. */
  useEffect(() => {
    if (!socket) return undefined;

    return socket.subscribe((event) => {
      if (event.type !== 'new_message' || !event.message) return;
      const incoming = event.message;
      const isMine = incoming.senderId === currentUserId;
      const isOpen = incoming.conversationId === selectedIdRef.current;

      if (!isMine && isOpen) {
        api.post(`/api/chat/conversations/${incoming.conversationId}/mark-read`).catch(() => {});
      }

      setConversations((current) => {
        const index = current.findIndex((c) => c.id === incoming.conversationId);

        // First contact from someone new: we have no row yet, so ask the server.
        if (index === -1) {
          refresh();
          return current;
        }

        const row = current[index];
        const next = [...current];
        next[index] = {
          ...row,
          lastMessage: (incoming.message || '').slice(0, 50) || 'Image',
          lastMessageAt: new Date(incoming.createdAt),
          unreadCount: isMine || isOpen ? row.unreadCount : row.unreadCount + 1,
          unread: isMine || isOpen ? row.unreadCount > 0 : true,
        };

        next.sort((a, b) => {
          if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
          return (b.lastMessageAt?.getTime() ?? 0) - (a.lastMessageAt?.getTime() ?? 0);
        });

        return next;
      });
    });
  }, [socket, currentUserId, refresh]);

  /* ---------------------------------------------------------------------
     Deep links.
     The links repointed during the cutover carry the thread they used to open:
       ?o=<orderId> | ?order=<orderId>          â†’ resolve/create via by-order
       ?c=<id> | ?conversationId=<id> | ?threadId=<id>  â†’ select that thread
     --------------------------------------------------------------------- */
  const focusConversation = useCallback(
    (conversation) => {
      if (!conversation?.id) return;
      setFocusOverride(conversation);
      setSelectedId(conversation.id);
      if ((conversation.unreadCount ?? 0) > 0) markAsRead(conversation.id);
    },
    [markAsRead],
  );

  /**
   * There is no conversation-detail endpoint, so when a `?c=` id is not in the
   * deduplicated inbox the counterparty is derived from the thread's own messages.
   */
  const resolveConversationById = useCallback(
    async (conversationId) => {
      const data = await api.get(`/api/chat/conversations/${conversationId}/messages?limit=20`);
      const fromOther = (data?.messages ?? []).find((m) => m.senderId !== currentUserId);

      return mapConversation({
        conversationId,
        listingId: data?.listingContext?.id ?? null,
        listingTitle: data?.listingContext?.title ?? null,
        otherPartyDisplayName: fromOther?.senderDisplayName ?? 'Conversation',
        otherPartyUsername: fromOther?.senderUsername ?? null,
        otherPartyRole: fromOther?.senderRole ?? null,
        otherPartyAvatar: null,
        otherPartyCountry: null,
        otherPartyId:
          (data?.messages ?? []).find((m) => m.senderId !== currentUserId)?.senderId ?? null,
        lastMessagePreview: data?.messages?.at(-1)?.message ?? null,
        lastMessageAt: data?.messages?.at(-1)?.createdAt ?? null,
        unreadCount: 0,
        otherUserLastSeenAt: data?.otherUserLastSeenAt ?? null,
      });
    },
    [currentUserId],
  );

  useEffect(() => {
    if (deepLinkHandled.current || !enabled || status !== 'ready') return;

    const params = new URLSearchParams(window.location.search);
    const conversationParam =
      params.get('c') || params.get('conversationId') || params.get('threadId');
    const orderParam = params.get('o') || params.get('order');

    if (!conversationParam && !orderParam) {
      deepLinkHandled.current = true;
      return;
    }
    deepLinkHandled.current = true;

    // Strip the params so a refresh does not replay the deep link.
    window.history.replaceState(null, '', window.location.pathname + window.location.hash);

    (async () => {
      try {
        if (orderParam) {
          // Resolves or creates the thread for that order, so this works even
          // before any conversation exists between the two accounts.
          const data = await api.get(
            `/api/chat/conversations/by-order/${encodeURIComponent(orderParam)}`,
          );
          focusConversation(mapConversation(data.conversation));
          return;
        }

        const fromList = conversations.find((c) => c.id === conversationParam);
        focusConversation(fromList ?? (await resolveConversationById(conversationParam)));
      } catch {
        // A stale or unauthorised deep link must not break the inbox.
      }
    })();
  }, [enabled, status, conversations, focusConversation, resolveConversationById]);

  const counts = useMemo(
    () => ({
      all: conversations.length,
      unread: conversations.filter((c) => c.unreadCount > 0).length,
      read: conversations.filter((c) => c.unreadCount === 0).length,
    }),
    [conversations],
  );

  const visibleConversations = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return conversations.filter((conversation) => {
      if (activeTab === 'unread' && conversation.unreadCount === 0) return false;
      if (activeTab === 'read' && conversation.unreadCount > 0) return false;
      if (query) {
        const haystack = `${conversation.name} ${conversation.username ?? ''}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [conversations, activeTab, searchQuery]);

  const selectedConversation = useMemo(() => {
    const fromList = conversations.find((c) => c.id === selectedId);
    if (fromList) return fromList;
    return focusOverride?.id === selectedId ? focusOverride : null;
  }, [conversations, selectedId, focusOverride]);

  const selectConversation = useCallback(
    (id) => {
      // A manual click always wins over a deep-linked thread.
      setFocusOverride(null);
      setSelectedId((current) => (current === id ? null : id));
      const row = conversations.find((c) => c.id === id);
      if (row?.unreadCount > 0) markAsRead(id);
    },
    [conversations, markAsRead],
  );

  const clearSelection = useCallback(() => setSelectedId(null), []);
  const resetFilters = useCallback(() => {
    setActiveTab('all');
    setSearchQuery('');
  }, []);

  return {
    side,
    conversations,
    visibleConversations,
    counts,
    totalUnreadMessages: conversations.reduce((sum, c) => sum + c.unreadCount, 0),
    status,
    error,
    refresh,

    activeTab,
    setActiveTab,
    searchQuery,
    setSearchQuery,

    selectedId,
    selectedConversation,
    selectConversation,
    clearSelection,
    markAsRead,

    resetFilters,
    isFiltered: activeTab !== 'all' || searchQuery.trim() !== '',

    // Recovery: chats hidden from this inbox, and the actions to bring them back.
    archivedChats,
    reloadArchived: loadArchived,
    restoreChat,
    restoreAllChats,
  };
}
